import React, { useState, useEffect, useReducer } from "react";
import { Card, CardBody } from "../card/card";
import EmojisCard from "../EmojisCard/EmojisCard";
import VoiceoverCard from "../VoiceoverCard/VoiceoverCard";
import QuizNavigation from "../QuizNavigation/QuizNavigation";
import NavigationButtons from "../NavigationButtons/NavigationButtons";
import ProgressOverview from "../ProgressOverview/ProgressOverview";
import useAutoSaveQuiz from "../../hooks/useAutoSave";
import useArrowNavigation from "../../hooks/useArrowNavigation";
import useVoiceGenerator from "../../hooks/useVoiceGenerator";
import quizReducer from "../../reducers/quiz";
import { initGuessByEmojiReducer } from "../../reducers/initial";

const GuessByEmojiQuiz = ({ quizId, onCreateQuiz, backendQuizData, channel }) => {
  // currentIndex: 0 represents the intro, and 1..N correspond to quiz items.
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [quizStructure, dispatch] = useReducer(quizReducer, initGuessByEmojiReducer);

  // Initialize voice generator using the channel's voiceId.
  const generateVoice = useVoiceGenerator(channel?.voiceId);

  // Load backend data if provided.
  useEffect(() => {
    if (backendQuizData) {
      dispatch({ type: "LOAD_QUIZ", payload: backendQuizData });
      setIsLoaded(true);
    }
  }, [backendQuizData]);

  // Trigger autosave only when data is loaded.
  useAutoSaveQuiz(quizId, isLoaded ? quizStructure : null);

  // Set up arrow key navigation.
  useArrowNavigation(
    () => setCurrentIndex((prev) => Math.max(0, prev - 1)),
    () =>
      setCurrentIndex((prev) =>
        Math.min(quizStructure.items.length, prev + 1)
      ),
    [quizStructure.items.length, isLoaded]
  );

  // --- Intro (Step 0) Handlers ---
  const handleTitleChange = (text) => {
    dispatch({ type: "UPDATE_TITLE", payload: { text } });
  };

  const handleIntroVoiceTextChange = (text) => {
    dispatch({ type: "UPDATE_INTRO_VOICE_TEXT", payload: { text } });
  };

  const handleGenerateIntroVoice = async () => {
    try {
      const voice = await generateVoice(quizStructure.introvoice.text);
      dispatch({ type: "UPDATE_INTRO_VOICE", payload: { voice } });
    } catch (error) {
      console.error("Error generating intro voice:", error);
    }
  };

  // --- Quiz Item Handlers ---
  const handleItemVoiceTextChange = (index, text) => {
    dispatch({ type: "UPDATE_ITEM_VOICE_TEXT", payload: { index, text } });
  };

  const handleGenerateItemVoice = async (index) => {
    try {
      const voice = await generateVoice(quizStructure.items[index].voice.text);
      dispatch({ type: "UPDATE_ITEM_VOICE", payload: { index, voice } });
    } catch (error) {
      console.error("Error generating item voice:", error);
    }
  };

  const handleItemAnswerTextChange = (index, text) => {
    dispatch({ type: "UPDATE_ITEM_ANSWER_TEXT", payload: { index, text } });
  };

  const handleItemNameChange = (index, text) => {
    dispatch({ type: "UPDATE_ITEM_NAME_TEXT", payload: { index, text } });
  };

  const handleEmojisChange = (index, emojis) => {
    dispatch({ type: "UPDATE_ITEM_EMOJIS", payload: { index, emojis } });
  };

  const handleFinalCreateQuiz = () => {
    onCreateQuiz(quizStructure);
  };

  // Disable the Create Quiz button if any required fields are missing.
  const shouldDisableCreateButton = () => {
    // Require generated voices for the intro and all items,
    // as well as necessary text fields.
    if (!quizStructure.introvoice.voice) return true;
    return quizStructure.items.some(
      (item) =>
        !item.voice.voice ||
        !item.name.text ||
        !item.emojis.length ||
        !item.emojis[0].prompt ||
        !item.answer.text
    );
  };

  const isItemComplete = (item) => {
    for (let emoji of item.emojis) {
      if (!emoji.image) return false;
    }
    return (
      item.voice?.voice &&
      item.answer?.voice
    );
  };

  return (
    <>
      <ProgressOverview
        quizStructure={quizStructure}
        currentIndex={currentIndex}
        onNavigate={setCurrentIndex}
        isItemComplete={isItemComplete}
      />

      {currentIndex === 0 && (
        <Card>
          <CardBody>
            <NavigationButtons
              onPrevious={() =>
                setCurrentIndex((prev) => Math.max(0, prev - 1))
              }
              onNext={() =>
                setCurrentIndex((prev) =>
                  Math.min(quizStructure.items.length, prev + 1)
                )
              }
              disablePrevious={currentIndex === 0}
              disableNext={quizStructure.items.length === 0 ? true : currentIndex === quizStructure.items.length}
            />
            <VoiceoverCard
              label="Generate Intro Voiceover"
              voiceover={quizStructure.introvoice}
              onTextChange={handleIntroVoiceTextChange}
              onGenerateVoice={handleGenerateIntroVoice}
              shortcutModifier="alt"
            />
          </CardBody>
        </Card>
      )}

      {currentIndex > 0 && (
        <Card>
          <CardBody>
            <QuizNavigation
              currentIndex={currentIndex}
              totalItems={quizStructure.items.length}
              onPrevious={() =>
                setCurrentIndex((prev) => Math.max(0, prev - 1))
              }
              onNext={() =>
                setCurrentIndex((prev) =>
                  Math.min(quizStructure.items.length, prev + 1)
                )
              }
            />
            {quizStructure.items[currentIndex - 1] && (
              <>
                <VoiceoverCard
                  label="Teaser Voiceover"
                  voiceover={quizStructure.items[currentIndex - 1].voice}
                  onTextChange={(text) =>
                    handleItemVoiceTextChange(currentIndex - 1, text)
                  }
                  onGenerateVoice={() =>
                    handleGenerateItemVoice(currentIndex - 1)
                  }
                  shortcutModifier="alt"
                />
                <VoiceoverCard
                  label="Answer Voiceover"
                  voiceover={quizStructure.items[currentIndex - 1].answer}
                  onTextChange={(text) =>
                    handleItemAnswerTextChange(currentIndex - 1, text)
                  }
                  onGenerateVoice={() =>
                    handleGenerateItemVoice(currentIndex - 1)
                  }
                  shortcutModifier="alt"
                />
                <EmojisCard
                  label="Correct Answer & Emojis"
                  name={quizStructure.items[currentIndex - 1].name.text}
                  onNameChange={(newName) =>
                    handleItemNameChange(currentIndex - 1, newName)
                  }
                  emojis={quizStructure.items[currentIndex - 1].emojis}
                  onEmojisChange={(newEmojis) =>
                    handleEmojisChange(currentIndex - 1, newEmojis)
                  }
                />
              </>
            )}
          </CardBody>
        </Card>
      )}

      <Card className="mt-3">
        <CardBody>
            <button
              className="btn btn-success w-100"
              disabled={shouldDisableCreateButton()}
              onClick={handleFinalCreateQuiz}
            >
              Create Quiz
            </button>
        </CardBody>
      </Card>
    </>
  );
};

export default GuessByEmojiQuiz;
